import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IEmployee } from '../interfaces/IEmployee';
import { IOnboardingEmployeeStatusResult } from '../interfaces/IOnboardingEmployeeStatusResult';
import { IEmployeeEmergencyContact } from '../interfaces/IEmployeeEmergencyContact';
import { IEmployeeListItem } from '../interfaces/IEmployeeListItem';
import { IWorkingHours } from '../interfaces/IWorkingHours';
import { IElmDraftPromotionResult } from '../interfaces/employee/IElmDraftPromotion';
import { EmployeeStatus } from '../enums/EmployeeStatus.enum';
import { ApiResult } from '../models/api-result.model';
import { EmployeeService } from '../services/employee.service';
import { IPaginatedResponse } from '../interfaces/IPaginatedResponse';
import {
  CheckEmployeeStatus,
  ClearOnboardingEmployeeStatus,
  ClearEmergencyContactsAndWorkingHours,
  CreateEmergencyContact,
  CreateEmployee,
  DeleteEmergencyContact,
  LoadEmergencyContacts,
  LoadEmployee,
  LoadEmployees,
  LoadAllEmployees,
  LoadWorkingHours,
  UpdateEmergencyContact,
  UpdateEmployee,
  UpdateWorkingHours,
  ResolveOnboardingEmployee,
  PromoteElmDraft,
} from './employee.actions';

export interface IEmployeeStateModel {
  employee: IEmployee;
  workingHours: IWorkingHours[];
  id: string;
  allEmployees: IEmployeeListItem[];
  employeePaginatedResponse: IPaginatedResponse<IEmployeeListItem>;
  emergencyContacts: IEmployeeEmergencyContact[];
  isEmployeePickerLoading: boolean;
  isLoading: boolean;
  isWorkingHoursLoading: boolean;
  onboardingEmployeeStatus: IOnboardingEmployeeStatusResult | null;
}

@State<IEmployeeStateModel>({
  name: 'employeeState',
  defaults: {
    employee: null,
    id: null,
    allEmployees: [],
    employeePaginatedResponse: null,
    emergencyContacts: [],
    workingHours: [],
    isEmployeePickerLoading: false,
    isLoading: false,
    isWorkingHoursLoading: false,
    onboardingEmployeeStatus: null,
  },
})
@Injectable()
export class EmployeeState {
  private employeeService = inject(EmployeeService);

  @Selector()
  static employee(state: IEmployeeStateModel): IEmployee {
    return state.employee;
  }

  @Selector()
  static employees(state: IEmployeeStateModel): IPaginatedResponse<IEmployeeListItem> {
    return state.employeePaginatedResponse;
  }

  @Selector()
  static allEmployees(state: IEmployeeStateModel): IEmployeeListItem[] {
    return state.allEmployees;
  }

  @Selector()
  static emergencyContacts(state: IEmployeeStateModel): IEmployeeEmergencyContact[] {
    return state.emergencyContacts;
  }

  @Selector()
  static isLoading(state: IEmployeeStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static isEmployeePickerLoading(state: IEmployeeStateModel): boolean {
    return state.isEmployeePickerLoading;
  }

  @Selector()
  static isWorkingHoursLoading(state: IEmployeeStateModel): boolean {
    return state.isWorkingHoursLoading;
  }

  @Selector()
  static workingHours(state: IEmployeeStateModel): IWorkingHours[] {
    return state.workingHours;
  }

  @Selector()
  static onboardingEmployeeStatus(state: IEmployeeStateModel): IOnboardingEmployeeStatusResult | null {
    return state.onboardingEmployeeStatus;
  }

  @Action(LoadEmployee)
  LoadEmployee(ctx: StateContext<IEmployeeStateModel>, { payload }: LoadEmployee): Observable<IEmployee> {
    this.setIsLoading(ctx, true);
    return this.employeeService.getEmployee(payload.id).pipe(
      tap((result: IEmployee) => {
        ctx.patchState({
          id: payload.id,
          employee: result,
        });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadEmergencyContacts)
  LoadEmergencyContacts(
    ctx: StateContext<IEmployeeStateModel>,
    { payload }: LoadEmergencyContacts,
  ): Observable<IEmployeeEmergencyContact[]> {
    this.setIsLoading(ctx, true);
    return this.employeeService.getEmergencyContacts(payload.id).pipe(
      tap((result: IEmployeeEmergencyContact[]) => {
        ctx.patchState({
          emergencyContacts: result,
        });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadEmployees)
  LoadEmployees(ctx: StateContext<IEmployeeStateModel>, payload: LoadEmployees): Observable<IPaginatedResponse<IEmployeeListItem>> {
    this.setIsEmployeePickerLoading(ctx, true);
    return this.employeeService.getEmployees(payload.employeePaginationProps).pipe(
      tap(result => {
        ctx.patchState({
          employeePaginatedResponse: result,
        });
      }),
      finalize(() => this.setIsEmployeePickerLoading(ctx, false)),
    );
  }

  @Action(LoadAllEmployees)
  LoadAllEmployees(ctx: StateContext<IEmployeeStateModel>): Observable<IEmployeeListItem[]> {
    this.setIsLoading(ctx, true);
    return this.employeeService.getAllPicker().pipe(
      tap((result: IEmployeeListItem[]) => {
        ctx.patchState({
          allEmployees: result,
        });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadWorkingHours)
  LoadWorkingHours(ctx: StateContext<IEmployeeStateModel>, { payload }: LoadWorkingHours): Observable<IWorkingHours[]> {
    this.setIsWorkingHoursLoading(ctx, true);
    return this.employeeService.getWorkingHours(payload.id).pipe(
      tap((result: IWorkingHours[]) => {
        ctx.patchState({
          id: payload.id,
          workingHours: result,
        });
      }),
      finalize(() => this.setIsWorkingHoursLoading(ctx, false)),
    );
  }

  @Action(UpdateEmployee)
  updateEmployee(ctx: StateContext<IEmployeeStateModel>, action: UpdateEmployee): Observable<ApiResult> {
    this.setIsLoading(ctx, true);
    return this.employeeService.updateEmployee(action.employee).pipe(finalize(() => this.setIsLoading(ctx, false)));
  }

  @Action(UpdateWorkingHours)
  updateWorkingHours(ctx: StateContext<IEmployeeStateModel>, action: UpdateWorkingHours): Observable<ApiResult> {
    return this.employeeService.updateWorkingHours(action.workingHours);
  }

  @Action(CreateEmployee)
  addEmployee(ctx: StateContext<IEmployeeStateModel>, action: CreateEmployee): Observable<ApiResult> {
    this.setIsLoading(ctx, true);
    return this.employeeService.addEmployee(action.employee).pipe(finalize(() => this.setIsLoading(ctx, false)));
  }

  @Action(CheckEmployeeStatus)
  checkEmployeeStatus(ctx: StateContext<IEmployeeStateModel>, action: CheckEmployeeStatus): Observable<ApiResult> {
    return this.employeeService.checkEmployeeStatus(action.gid).pipe(
      tap((result: ApiResult) => {
        action.result = (result?.type as EmployeeStatus) ?? null;
      }),
    );
  }

  @Action(ResolveOnboardingEmployee)
  resolveOnboardingEmployee(
    ctx: StateContext<IEmployeeStateModel>,
    action: ResolveOnboardingEmployee,
  ): Observable<IOnboardingEmployeeStatusResult> {
    return this.employeeService.checkEmployeeStatus(action.gid, true).pipe(
      map(result => (Object.values(EmployeeStatus).includes(result.type as EmployeeStatus) ? (result.type as EmployeeStatus) : null)),
      switchMap(status =>
        status === EmployeeStatus.Active
          ? this.employeeService.getEmployeeByGID(action.gid, undefined, true).pipe(map(employee => ({ status, employee })))
          : of({ status }),
      ),
      tap(onboardingEmployeeStatus => ctx.patchState({ onboardingEmployeeStatus })),
    );
  }

  @Action(ClearOnboardingEmployeeStatus)
  clearOnboardingEmployeeStatus(ctx: StateContext<IEmployeeStateModel>): void {
    ctx.patchState({ onboardingEmployeeStatus: null });
  }

  @Action(PromoteElmDraft)
  promoteElmDraft(ctx: StateContext<IEmployeeStateModel>, action: PromoteElmDraft): Observable<IElmDraftPromotionResult> {
    this.setIsLoading(ctx, true);
    return this.employeeService.promoteElmDraft(action.request).pipe(finalize(() => this.setIsLoading(ctx, false)));
  }

  @Action(CreateEmergencyContact)
  addEmergencyContact(
    ctx: StateContext<IEmployeeStateModel>,
    { payload }: CreateEmergencyContact,
  ): Observable<ApiResult | HttpErrorResponse> {
    this.setIsLoading(ctx, true);
    return this.employeeService.addEmergencyContact(payload.id, payload.contact).pipe(finalize(() => this.setIsLoading(ctx, false)));
  }

  @Action(UpdateEmergencyContact)
  updateEmergencyContact(
    ctx: StateContext<IEmployeeStateModel>,
    { payload }: UpdateEmergencyContact,
  ): Observable<ApiResult | HttpErrorResponse> {
    this.setIsLoading(ctx, true);
    return this.employeeService.updateEmergencyContact(payload.id, payload.contact).pipe(finalize(() => this.setIsLoading(ctx, false)));
  }

  @Action(DeleteEmergencyContact)
  deleteEmergencyContact(
    ctx: StateContext<IEmployeeStateModel>,
    { payload }: DeleteEmergencyContact,
  ): Observable<ApiResult | HttpErrorResponse> {
    this.setIsLoading(ctx, true);

    return this.employeeService
      .deleteEmergencyContact(payload.id, payload.emergencyContactId)
      .pipe(finalize(() => this.setIsLoading(ctx, false)));
  }

  @Action(ClearEmergencyContactsAndWorkingHours)
  clearEmergencyContactsAndWorkingHours(ctx: StateContext<IEmployeeStateModel>): void {
    ctx.patchState({
      workingHours: [],
      emergencyContacts: [],
    });
  }

  private setIsLoading(ctx: StateContext<IEmployeeStateModel>, isLoading: boolean): void {
    ctx.patchState({ isLoading });
  }

  private setIsEmployeePickerLoading(ctx: StateContext<IEmployeeStateModel>, isEmployeePickerLoading: boolean): void {
    ctx.patchState({ isEmployeePickerLoading });
  }

  private setIsWorkingHoursLoading(ctx: StateContext<IEmployeeStateModel>, isWorkingHoursLoading: boolean): void {
    ctx.patchState({ isWorkingHoursLoading });
  }
}
